import {
  couponApplicabilityTypes,
  detectCouponApplicability,
  isAnnualOnlyCoupon,
  signupPlanIntervals,
} from './signupPricing';

const getNestedPlan = plan =>
  plan?.plan || plan?.price || plan?.items?.data?.[0]?.price || plan?.items?.data?.[0]?.plan || null;

const getPlanInterval = plan => {
  if (!plan) {
    return null;
  }

  return plan?.interval || plan?.recurring_interval || plan?.recurring?.interval || getPlanInterval(getNestedPlan(plan));
};

export const getCurrentSubscriptionPlan = currentSubscription => {
  if (!currentSubscription) {
    return null;
  }

  return (
    currentSubscription?.plan ||
    currentSubscription?.items?.data?.[0]?.price ||
    currentSubscription?.items?.data?.[0]?.plan ||
    currentSubscription
  );
};

export const getCurrentSubscriptionInterval = currentSubscription =>
  getPlanInterval(getCurrentSubscriptionPlan(currentSubscription));

export const shouldForceWebAnnualPlan = ({
  coupon,
  couponCode,
  annualCouponCodes = [],
  couponValidated = false,
  isCorporateUser = false,
} = {}) => {
  if (isCorporateUser) {
    return true;
  }

  if (!couponValidated) {
    return false;
  }

  return (
    detectCouponApplicability(coupon) === couponApplicabilityTypes.ANNUAL ||
    isAnnualOnlyCoupon({ coupon, couponCode, annualCouponCodes })
  );
};

export const shouldSurfaceWebAnnualPlan = ({
  currentSubscription,
  coupon,
  couponCode,
  annualCouponCodes = [],
  couponValidated = false,
  isCorporateUser = false,
} = {}) => {
  if (
    shouldForceWebAnnualPlan({
      coupon,
      couponCode,
      annualCouponCodes,
      couponValidated,
      isCorporateUser,
    })
  ) {
    return true;
  }

  return getCurrentSubscriptionInterval(currentSubscription) === signupPlanIntervals.YEAR;
};

export const getVisibleWebSubscriptionPlans = ({
  productPrices = [],
  currentSubscription,
  coupon,
  couponCode,
  annualCouponCodes = [],
  couponValidated = false,
  isCorporateUser = false,
} = {}) => {
  const plans = Array.isArray(productPrices) ? productPrices.filter(Boolean) : [];

  if (
    shouldForceWebAnnualPlan({
      coupon,
      couponCode,
      annualCouponCodes,
      couponValidated,
      isCorporateUser,
    })
  ) {
    return plans.filter(plan => getPlanInterval(plan) === signupPlanIntervals.YEAR);
  }

  if (
    shouldSurfaceWebAnnualPlan({
      currentSubscription,
      coupon,
      couponCode,
      annualCouponCodes,
      couponValidated,
      isCorporateUser,
    })
  ) {
    return plans;
  }

  return plans.filter(plan => getPlanInterval(plan) !== signupPlanIntervals.YEAR);
};

export const getDefaultWebSubscriptionPlan = ({
  productPrices = [],
  preferredInterval = null,
  currentSubscription,
  coupon,
  couponCode,
  annualCouponCodes = [],
  couponValidated = false,
  isCorporateUser = false,
} = {}) => {
  const visiblePlans = getVisibleWebSubscriptionPlans({
    productPrices,
    currentSubscription,
    coupon,
    couponCode,
    annualCouponCodes,
    couponValidated,
    isCorporateUser,
  });

  const currentInterval = getCurrentSubscriptionInterval(currentSubscription);
  const preferredIntervals = [
    preferredInterval,
    currentInterval,
    shouldForceWebAnnualPlan({
      coupon,
      couponCode,
      annualCouponCodes,
      couponValidated,
      isCorporateUser,
    })
      ? signupPlanIntervals.YEAR
      : null,
    signupPlanIntervals.MONTH,
    signupPlanIntervals.TRIAL,
    signupPlanIntervals.YEAR,
  ].filter(Boolean);

  for (const interval of preferredIntervals) {
    const match = visiblePlans.find(plan => getPlanInterval(plan) === interval);
    if (match) {
      return match;
    }
  }

  return visiblePlans[0] || null;
};

export const mapPlanIntervalToWebSelection = interval => {
  if (interval === signupPlanIntervals.MONTH) {
    return 'monthly';
  }

  if (interval === signupPlanIntervals.YEAR) {
    return 'annual';
  }

  return null;
};
